import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Menu, X, Home, BookOpen, Brain, Code2, Rocket, Target, FlaskConical, Lightbulb, GraduationCap, ExternalLink } from 'lucide-react';
import { Button } from './components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from './components/ui/sheet';
import { ScrollArea } from './components/ui/scroll-area';

// Slides imports
import TitleSlide from './slides/TitleSlide';
import ObjectivesSlide from './slides/ObjectivesSlide';
import LLMFoundationsSlide from './slides/LLMFoundationsSlide';
import TransformerSlide from './slides/TransformerSlide';
import TrainingPipelineSlide from './slides/TrainingPipelineSlide';
import MoESlide from './slides/MoESlide';
import WhatIsReasoningSlide from './slides/WhatIsReasoningSlide';
import CoTSlide from './slides/CoTSlide';
import SelfConsistencySlide from './slides/SelfConsistencySlide';
import TreeOfThoughtsSlide from './slides/TreeOfThoughtsSlide';
import RLVRSlide from './slides/RLVRSlide';
import GRPOSlide from './slides/GRPOSlide';
import InferenceComputeSlide from './slides/InferenceComputeSlide';
// Vendor Comparison Slides
import VendorComparisonSlide from './slides/VendorComparisonSlide';
import DeepSeekApproachSlide from './slides/DeepSeekApproachSlide';
import OpenAIApproachSlide from './slides/OpenAIApproachSlide';
import OtherVendorsSlide from './slides/OtherVendorsSlide';
import ComparisonTableSlide from './slides/ComparisonTableSlide';
// DeepSeek Deep Dive Slides
import R1ZeroSlide from './slides/R1ZeroSlide';
import FourStagePipelineSlide from './slides/FourStagePipelineSlide';
import DistillationSlide from './slides/DistillationSlide';
import ModelFamilySlide from './slides/ModelFamilySlide';
import WhatDidntWorkSlide from './slides/WhatDidntWorkSlide';
// CoT Evolution Slides
import CoTPretrainingSlide from './slides/CoTPretrainingSlide';
import AhaMomentSlide from './slides/AhaMomentSlide';
// Remaining slides
import NOCApplicationsSlide from './slides/NOCApplicationsSlide';
import ConfigGenerationSlide from './slides/ConfigGenerationSlide';
import LimitationsSlide from './slides/LimitationsSlide';
import FutureDirectionsSlide from './slides/FutureDirectionsSlide';
import DeploymentSlide from './slides/DeploymentSlide';
import HandsOnSlide from './slides/HandsOnSlide';
import KeyTakeawaysSlide from './slides/KeyTakeawaysSlide';
import ResourcesSlide from './slides/ResourcesSlide';
import PaperReferencesSlide from './slides/PaperReferencesSlide';
import ThankYouSlide from './slides/ThankYouSlide';

const slides = [
  { id: 0, title: 'Cover', component: TitleSlide, section: 'intro', icon: Home },
  { id: 1, title: 'Learning Objectives', component: ObjectivesSlide, section: 'intro', icon: Target },
  { id: 2, title: 'LLM Foundations', component: LLMFoundationsSlide, section: 'foundations', icon: BookOpen },
  { id: 3, title: 'Transformer Architecture', component: TransformerSlide, section: 'foundations', icon: BookOpen },
  { id: 4, title: 'Training Pipeline', component: TrainingPipelineSlide, section: 'foundations', icon: BookOpen },
  { id: 5, title: 'Mixture of Experts', component: MoESlide, section: 'foundations', icon: BookOpen },
  { id: 6, title: 'What is Reasoning?', component: WhatIsReasoningSlide, section: 'reasoning', icon: Brain },
  { id: 7, title: 'Chain-of-Thought', component: CoTSlide, section: 'reasoning', icon: Brain },
  { id: 8, title: 'Self-Consistency', component: SelfConsistencySlide, section: 'reasoning', icon: Brain },
  { id: 9, title: 'Tree-of-Thoughts', component: TreeOfThoughtsSlide, section: 'reasoning', icon: Brain },
  { id: 10, title: 'RLVR Paradigm', component: RLVRSlide, section: 'training', icon: Code2 },
  { id: 11, title: 'GRPO Algorithm', component: GRPOSlide, section: 'training', icon: Code2 },
  { id: 12, title: 'Inference Compute', component: InferenceComputeSlide, section: 'training', icon: Code2 },
  // Vendor Comparison Section
  { id: 13, title: 'Industry CoT Overview', component: VendorComparisonSlide, section: 'vendors', icon: Target },
  { id: 14, title: 'DeepSeek Approach', component: DeepSeekApproachSlide, section: 'vendors', icon: Target },
  { id: 15, title: 'OpenAI Approach', component: OpenAIApproachSlide, section: 'vendors', icon: Target },
  { id: 16, title: 'Other Vendors', component: OtherVendorsSlide, section: 'vendors', icon: Target },
  { id: 17, title: 'Vendor Comparison', component: ComparisonTableSlide, section: 'vendors', icon: Target },
  // DeepSeek Deep Dive Section
  { id: 18, title: 'R1-Zero: Proof of Concept', component: R1ZeroSlide, section: 'deepseek', icon: Brain },
  { id: 19, title: 'R1: 4-Stage Pipeline', component: FourStagePipelineSlide, section: 'deepseek', icon: Code2 },
  { id: 20, title: 'The Aha Moment', component: AhaMomentSlide, section: 'deepseek', icon: Lightbulb },
  { id: 21, title: 'Distillation Results', component: DistillationSlide, section: 'deepseek', icon: Target },
  { id: 22, title: 'Model Family Evolution', component: ModelFamilySlide, section: 'deepseek', icon: BookOpen },
  { id: 23, title: 'What Didn\'t Work', component: WhatDidntWorkSlide, section: 'deepseek', icon: Lightbulb },
  // CoT Evolution Section
  { id: 24, title: 'CoT Evolution', component: CoTPretrainingSlide, section: 'evolution', icon: Brain },
  // Applications
  { id: 25, title: 'NOC Applications', component: NOCApplicationsSlide, section: 'applications', icon: Rocket },
  { id: 26, title: 'Config Generation', component: ConfigGenerationSlide, section: 'applications', icon: Rocket },
  // Future & Challenges
  { id: 27, title: 'Limitations', component: LimitationsSlide, section: 'future', icon: Lightbulb },
  { id: 28, title: 'Future Directions', component: FutureDirectionsSlide, section: 'future', icon: Lightbulb },
  { id: 29, title: 'Deployment Roadmap', component: DeploymentSlide, section: 'future', icon: Lightbulb },
  // Practice & Conclusion
  { id: 30, title: 'Hands-On Exercise', component: HandsOnSlide, section: 'practice', icon: FlaskConical },
  { id: 31, title: 'Key Takeaways', component: KeyTakeawaysSlide, section: 'conclusion', icon: GraduationCap },
  { id: 32, title: 'Paper References', component: PaperReferencesSlide, section: 'conclusion', icon: ExternalLink },
  { id: 33, title: 'Resources', component: ResourcesSlide, section: 'conclusion', icon: ExternalLink },
  { id: 34, title: 'Thank You', component: ThankYouSlide, section: 'conclusion', icon: GraduationCap },
];

const sections = [
  { id: 'intro', label: 'Introduction', color: 'primary' },
  { id: 'foundations', label: 'LLM Foundations', color: 'secondary' },
  { id: 'reasoning', label: 'Reasoning Models', color: 'accent' },
  { id: 'training', label: 'Training', color: 'chart-4' },
  { id: 'vendors', label: 'Vendor Approaches', color: 'chart-5' },
  { id: 'deepseek', label: 'DeepSeek Deep Dive', color: 'primary' },
  { id: 'evolution', label: 'CoT Evolution', color: 'chart-1' },
  { id: 'applications', label: 'Applications', color: 'chart-2' },
  { id: 'future', label: 'Future & Challenges', color: 'chart-3' },
  { id: 'practice', label: 'Practice', color: 'secondary' },
  { id: 'conclusion', label: 'Conclusion', color: 'accent' },
];

function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length && !isAnimating) {
      setIsAnimating(true);
      setCurrentSlide(index);
      setTimeout(() => setIsAnimating(false), 600);
    }
  }, [isAnimating]);

  const nextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToSlide(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        goToSlide(slides.length - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, goToSlide]);

  // Touch navigation
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) nextSlide();
        else prevSlide();
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [nextSlide, prevSlide]);

  const progress = ((currentSlide + 1) / slides.length) * 100;
  const CurrentSlideComponent = slides[currentSlide].component;
  const currentSection = sections.find(s => s.id === slides[currentSlide].section);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden relative">
      {/* Progress bar */}
      <div className="progress-bar" style={{ width: `${progress}%` }} />
      
      {/* Background effects */}
      <div className="fixed inset-0 bg-grid opacity-30 pointer-events-none" />
      <div 
        className="fixed inset-0 pointer-events-none opacity-50"
        style={{ background: 'var(--gradient-glow)' }}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 md:px-6 py-4">
        <div className="flex items-center gap-3">
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 bg-card border-border">
              <div className="py-4">
                <h2 className="font-heading text-lg font-semibold mb-4 text-foreground">Slides</h2>
                <ScrollArea className="h-[calc(100vh-120px)]">
                  <div className="space-y-1 pr-4">
                    {sections.map(section => (
                      <div key={section.id} className="mb-4">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
                          {section.label}
                        </div>
                        {slides.filter(s => s.section === section.id).map(slide => {
                          const Icon = slide.icon;
                          return (
                            <button
                              key={slide.id}
                              onClick={() => {
                                goToSlide(slide.id);
                                setIsMenuOpen(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                                currentSlide === slide.id 
                                  ? 'bg-primary/20 text-primary' 
                                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                              }`}
                            >
                              <Icon className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{slide.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </SheetContent>
          </Sheet>
          
          <div className="hidden md:block">
            <span className="text-xs text-muted-foreground">Section</span>
            <div className="text-sm font-medium text-foreground">{currentSection?.label}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-mono">{currentSlide + 1}</span>
          <span>/</span>
          <span className="font-mono">{slides.length}</span>
        </div>
      </header>

      {/* Section indicator (desktop) */}
      <div className="section-indicator">
        {sections.map((section, index) => {
          const sectionSlides = slides.filter(s => s.section === section.id);
          const isActive = slides[currentSlide].section === section.id;
          return (
            <button
              key={section.id}
              onClick={() => goToSlide(sectionSlides[0].id)}
              className={`nav-dot ${isActive ? 'active' : ''}`}
              title={section.label}
            />
          );
        })}
      </div>

      {/* Main slide content */}
      <main className="relative z-10">
        <CurrentSlideComponent isActive={true} slideNumber={currentSlide + 1} />
      </main>

      {/* Navigation controls */}
      <div className="fixed bottom-6 left-0 right-0 z-40 flex items-center justify-center gap-4 px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="w-12 h-12 rounded-full bg-card/80 backdrop-blur border border-border hover:bg-card hover:border-primary/50 disabled:opacity-30"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <div className="hidden md:flex items-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'w-6 bg-primary' 
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
            />
          ))}
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="w-12 h-12 rounded-full bg-card/80 backdrop-blur border border-border hover:bg-card hover:border-primary/50 disabled:opacity-30"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Keyboard hints */}
      <div className="fixed bottom-6 right-6 hidden lg:flex items-center gap-2 text-xs text-muted-foreground">
        <kbd className="kbd">←</kbd>
        <kbd className="kbd">→</kbd>
        <span>Navigate</span>
      </div>
    </div>
  );
}

export default App;
